import { Link } from "@inertiajs/react";
import { useState } from "react";
import { ArrowLeft, ChevronDown, ChevronUp, Loader2, Plus, Save, Trash2 } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";

type Channel = "cover_letter" | "skm" | "memo" | "resume";

interface VoiceExample {
  id: number;
  channel: Channel;
  title: string;
  example_text: string;
  input_text: string | null;
  source: string;
  position: number;
  active: boolean;
}

interface Props {
  examples: VoiceExample[];
}

const CHANNELS: { key: Channel; label: string; desc: string }[] = [
  { key: "cover_letter", label: "Cover Letter", desc: "Paired with the resume it was written for." },
  { key: "skm",          label: "Skill Marketing (SKM)", desc: "Anonymized firm-outreach blasts." },
  { key: "memo",         label: "Memo", desc: "Internal submission memos." },
  { key: "resume",       label: "Resume", desc: "Format exemplars for the resume optimizer." },
];

function csrfToken(): string {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
}

async function apiRequest(url: string, method: string, body?: unknown) {
  const res = await fetch(url, {
    method,
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = res.status === 204 ? null : await res.json();
  if (!res.ok) throw new Error(data?.errors ? JSON.stringify(data.errors) : "Request failed");
  return data;
}

function ExampleEditor({ example: initial, onDeleted }: { example: VoiceExample; onDeleted: (id: number) => void }) {
  const [open, setOpen]         = useState(false);
  const [example, setExample]   = useState(initial);
  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saved, setSaved]       = useState(false);
  const [error, setError]       = useState<string | null>(null);

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const data = await apiRequest(`/settings/voice-examples/${example.id}`, "PATCH", {
        voice_example: {
          title:        example.title,
          example_text: example.example_text,
          input_text:   example.input_text,
          active:       example.active,
        },
      });
      setExample(data as VoiceExample);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirm(`Remove "${example.title || "this example"}"?`)) return;
    setDeleting(true);
    try {
      await apiRequest(`/settings/voice-examples/${example.id}`, "DELETE");
      onDeleted(example.id);
    } catch {
      setError("Delete failed. Try again.");
      setDeleting(false);
    }
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className={`w-2 h-2 rounded-full shrink-0 ${example.active ? "bg-green-500" : "bg-gray-300"}`} />
          <span className="text-sm font-medium text-gray-900 truncate">{example.title || "Untitled example"}</span>
          {example.source && <span className="text-xs text-gray-400 truncate shrink-0">{example.source}</span>}
        </div>
        {open ? <ChevronUp size={14} className="text-gray-400 shrink-0" /> : <ChevronDown size={14} className="text-gray-400 shrink-0" />}
      </button>

      {open && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 space-y-3 bg-white">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={example.active}
                onChange={(e) => setExample((ex) => ({ ...ex, active: e.target.checked }))}
                className="rounded border-gray-300"
              />
              Active
            </label>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
            <input
              value={example.title}
              onChange={(e) => setExample((ex) => ({ ...ex, title: e.target.value }))}
              className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          {example.channel === "cover_letter" && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Paired Resume Text <span className="text-gray-400 font-normal">(optional — what the letter below was written from)</span>
              </label>
              <textarea
                value={example.input_text ?? ""}
                onChange={(e) => setExample((ex) => ({ ...ex, input_text: e.target.value }))}
                rows={6}
                className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Example Text</label>
            <textarea
              value={example.example_text}
              onChange={(e) => setExample((ex) => ({ ...ex, example_text: e.target.value }))}
              rows={10}
              className="w-full border border-gray-200 rounded px-3 py-1.5 text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}

          <div className="flex items-center justify-between">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-700 text-white rounded-md hover:bg-green-800 disabled:opacity-50 transition-colors"
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              {saving ? "Scrubbing & saving…" : saved ? "Saved!" : "Save"}
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
            >
              <Trash2 size={13} /> Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AddExampleForm({ channel, onAdded }: { channel: Channel; onAdded: (e: VoiceExample) => void }) {
  const [title, setTitle]             = useState("");
  const [exampleText, setExampleText] = useState("");
  const [inputText, setInputText]     = useState("");
  const [adding, setAdding]           = useState(false);
  const [error, setError]             = useState<string | null>(null);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!exampleText.trim()) return;
    setAdding(true);
    setError(null);
    try {
      const data = await apiRequest("/settings/voice-examples", "POST", {
        voice_example: {
          channel,
          title:        title.trim(),
          example_text: exampleText,
          input_text:   channel === "cover_letter" ? inputText : undefined,
          active:       true,
        },
      });
      onAdded(data as VoiceExample);
      setTitle("");
      setExampleText("");
      setInputText("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to add example.");
    } finally {
      setAdding(false);
    }
  }

  return (
    <form onSubmit={handleAdd} className="border border-dashed border-gray-300 rounded-lg p-4 bg-gray-50 space-y-3">
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title (optional)"
        className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
      />
      {channel === "cover_letter" && (
        <textarea
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          rows={4}
          placeholder="Paired resume text this letter was written from (optional)"
          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
        />
      )}
      <textarea
        value={exampleText}
        onChange={(e) => setExampleText(e.target.value)}
        rows={6}
        placeholder="Paste the example text — real names/firms are scrubbed automatically before saving"
        className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
      <button
        type="submit"
        disabled={adding || !exampleText.trim()}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-md transition-colors"
      >
        {adding ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
        {adding ? "Scrubbing & saving…" : "Add Example"}
      </button>
    </form>
  );
}

export default function VoiceExamples({ examples: initial }: Props) {
  const [examples, setExamples] = useState<VoiceExample[]>(initial);

  function handleAdded(example: VoiceExample) {
    setExamples((prev) => [...prev, example]);
  }

  function handleDeleted(id: number) {
    setExamples((prev) => prev.filter((e) => e.id !== id));
  }

  return (
    <AppLayout title="Voice Examples">
      <div className="max-w-2xl space-y-5">
        <Link href="/settings" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft size={14} />
          Settings
        </Link>

        <div>
          <h1 className="text-xl font-semibold text-gray-900">Voice Examples</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Real writing samples the AI drafts from — cover letters, SKM blasts, memos, and resumes. Add your own
            anytime; names and firms are scrubbed to placeholders automatically before anything is saved.
          </p>
        </div>

        {CHANNELS.map(({ key, label, desc }) => {
          const group = examples
            .filter((e) => e.channel === key)
            .sort((a, b) => a.position - b.position || a.id - b.id);

          return (
            <div key={key}>
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{label}</h2>
              <p className="text-xs text-gray-400 mb-2">{desc}</p>
              <div className="space-y-2 mb-3">
                {group.length === 0 && (
                  <p className="text-xs text-gray-300 italic px-1">No examples yet.</p>
                )}
                {group.map((e) => (
                  <ExampleEditor key={e.id} example={e} onDeleted={handleDeleted} />
                ))}
              </div>
              <AddExampleForm channel={key} onAdded={handleAdded} />
            </div>
          );
        })}
      </div>
    </AppLayout>
  );
}
